import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TESTS_POLLING,
  TEST_OVERVIEW,
  DASHBOARD,
  TEST_MANAGEMENT_TESTS,
  TEST_CONFIGURATION,
  TEST_OVERVIEW_SIDEPANEL_EXECUTION_HISTORY,
} from '@elementary/common-ui/constants/queryKeys';
import { useRecoilState } from 'recoil';
import { pollTestsBulk } from 'shared/services/api';
import * as atoms from 'shared/store/atoms';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';

const useTestsPolling = (initPolling: boolean = false) => {
  const selectedEnvId = useSelectedEnv();
  const queryClient = useQueryClient();

  const [pollingTestsIds, setPollingTestsIds] = useRecoilState(
    atoms.pollingTests,
  );

  const pollTests = useCallback(async () => {
    if (pollingTestsIds.size > 0) {
      const testIds = [...pollingTestsIds];
      try {
        const response = await pollTestsBulk(selectedEnvId!, testIds);
        return response.results.map((result, index) => ({
          testId: testIds[index],
          executed_with_new_config: result?.executed_with_new_config || false,
          latest_result_id: result?.latest_result_id,
        }));
      } catch (error) {
        return testIds.map((testId) => ({
          testId,
          executed_with_new_config: false,
          latest_result_id: undefined,
        }));
      }
    }
    return [];
  }, [selectedEnvId, pollingTestsIds]);

  useQuery([TESTS_POLLING, selectedEnvId], () => pollTests(), {
    enabled: initPolling && !!selectedEnvId && pollingTestsIds.size > 0,
    refetchInterval: 10000,
    keepPreviousData: true,
    onSuccess: (data) => {
      let shouldRefetchCloudTests = false;

      data.forEach((item) => {
        if (item?.executed_with_new_config && item?.latest_result_id) {
          setPollingTestsIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(item.testId);
            return newSet;
          });

          queryClient.invalidateQueries([TEST_OVERVIEW, item?.testId]);
          queryClient.invalidateQueries([
            TEST_OVERVIEW_SIDEPANEL_EXECUTION_HISTORY,
            item?.testId,
          ]);
          queryClient.invalidateQueries([TEST_CONFIGURATION, item?.testId]);
          shouldRefetchCloudTests = true;
        }
      });

      if (shouldRefetchCloudTests) {
        queryClient.invalidateQueries([TEST_MANAGEMENT_TESTS, selectedEnvId], {
          exact: false,
        });
        queryClient.invalidateQueries([DASHBOARD, selectedEnvId]);
      }
    },
  });

  return {
    pollingTestsIds,
    setPollingTestsIds,
  };
};

export default useTestsPolling;
